// API service for Prenup application
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5003/api';

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  // Helper method to get auth headers
  getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : ''
    };
  }

  // Generic fetch method
  async fetchData(endpoint, options = {}) {
    // Ensure baseURL is valid
    if (!this.baseURL || this.baseURL === '') {
      this.baseURL = 'http://localhost:5003/api';
    }
    
    const url = `${this.baseURL}${endpoint}`;
    console.log('API Request URL:', url);
    const config = {
      headers: this.getAuthHeaders(),
      ...options
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();
      
      if (!response.ok) {
        // Create a proper error object with response data
        const error = new Error(data.message || `HTTP error! status: ${response.status}`);
        error.response = {
          data: data,
          status: response.status
        };
        throw error;
      }
      
      return data;
    } catch (error) {
      console.error('API Error:', error);
      // If error already has response data, throw as is
      if (error.response) {
        throw error;
      }
      // Otherwise, create a new error with the original error message
      const newError = new Error(error.message);
      newError.response = {
        data: { message: error.message },
        status: 500
      };
      throw newError;
    }
  }

  // Authentication methods
  async loginUser(credentials) {
    return this.fetchData('/auth/user/login', {
      method: 'POST',
      body: JSON.stringify(credentials)
    });
  }

  async registerUser(userData) {
    return this.fetchData('/auth/user/register', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
  }

  async getUserProfile() {
    return this.fetchData('/auth/profile');
  }

  async changePassword(passwordData) {
    return this.fetchData('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify(passwordData)
    });
  }

  // Prenup methods
  async getDashboardStats() {
    return this.fetchData('/prenups/dashboard/stats');
  }

  async getPrenups(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.fetchData(`/prenups${queryString ? `?${queryString}` : ''}`);
  }

  async getPrenupById(id) {
    return this.fetchData(`/prenups/${id}`);
  }

  async createPrenup(prenupData) {
    try {
      return await this.fetchData('/prenups', {
        method: 'POST',
        body: JSON.stringify(prenupData)
      });
    } catch (error) {
      console.error('API createPrenup error:', error);
      throw error;
    }
  }

  async updatePrenup(id, prenupData) {
    return this.fetchData(`/prenups/${id}`, {
      method: 'PUT',
      body: JSON.stringify(prenupData)
    });
  }

  async deletePrenup(id) {
    return this.fetchData(`/prenups/${id}`, {
      method: 'DELETE'
    });
  }

  async searchPrenups(searchParams) {
    const queryString = new URLSearchParams(searchParams).toString();
    return this.fetchData(`/prenups/search?${queryString}`);
  }

  async validateBarcode(barcode) {
    return this.fetchData('/prenups/validate-barcode', {
      method: 'POST',
      body: JSON.stringify({ barcode })
    });
  }

  async searchPartnerByNID(nid) {
    return this.fetchData(`/prenups/search-partner/${nid}`);
  }

  async searchUserByNID(nid) {
    return this.fetchData(`/prenups/search-partner/${nid}`);
  }

  // Reference data methods
  async getReferenceData() {
    return this.fetchData('/reference/form-data');
  }

  async getNationalities() {
    return this.fetchData('/reference/nationalities');
  }

  async getBloodTypes() {
    return this.fetchData('/reference/blood-types');
  }

  async getGovernorates() {
    return this.fetchData('/reference/governorates');
  }
}

// Create and export instance
const apiService = new ApiService();
export default apiService;
